import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import cashflowProjectionService, {
  formatCompactCurrency,
  formatCurrency,
  formatDate,
} from '../../services/cashflowProjectionService';
import { getSuppliers } from '../../services/procurementService';

const ALL_TIERS = ['committed', 'expected', 'projected'];

const categoryLabels = {
  ap_scheduled: 'Scheduled AP',
  po_open_milestones: 'Open PO milestones',
  ar_wholesale: 'Wholesale AR',
  marketplace_in_transit: 'Marketplace in transit',
  amex_payment: 'Amex payments',
  revenue_projected: 'Projected revenue',
  recurring_vendors: 'Recurring vendors',
  reorder_pos: 'Reorder POs',
  planned_pos: 'Planned POs',
  vat_gst: 'VAT/GST',
  marketing_ap: 'Marketing platforms (AP)',
  opex_override: 'Manual OpEx',
};

const projectedCategories = new Set([
  'revenue_projected',
  'recurring_vendors',
  'reorder_pos',
  'planned_pos',
  'vat_gst',
  'marketing_ap',
  'opex_override',
]);

function total(obj) {
  return Object.values(obj || {}).reduce((sum, v) => sum + Number(v || 0), 0);
}

function categoryTotal(row, category) {
  return Number(row.inflows?.[category] || 0) - Number(row.outflows?.[category] || 0);
}

function toJsonText(value, fallback) {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch (_) {
    return JSON.stringify(fallback, null, 2);
  }
}

function parseJsonField(value, label, fallback, validate) {
  if (String(value || '').trim() === '') return fallback;
  try {
    const parsed = JSON.parse(value);
    if (validate && !validate(parsed)) {
      throw new Error(`${label} has the wrong shape`);
    }
    return parsed;
  } catch (e) {
    if (e.message.includes('wrong shape')) throw e;
    throw new Error(`${label} must be valid JSON`);
  }
}

function parseFiniteNumber(value, label, fallback = 0, options = {}) {
  if (value === '' || value === null || value === undefined) {
    if (options.allowNull) return null;
    return fallback;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) throw new Error(`${label} must be a number`);
  if (options.min !== undefined && n < options.min) {
    throw new Error(`${label} must be at least ${options.min}`);
  }
  if (options.max !== undefined && n > options.max) {
    throw new Error(`${label} must be at most ${options.max}`);
  }
  return n;
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Per-event tier split (2026-08-14): the backend reports the projected share
// per category per bucket (projected_in / projected_out), so the documented vs
// projected chart split follows each EVENT's own confidence — a category can
// mix tiers (vat_gst: next filing 'expected', repeats 'projected';
// marketplace_in_transit mixes exact payouts with run-rate estimates). The
// legacy category Set is only the fallback for a response without the maps.
function chartTotal(obj, projected, projectedMap) {
  if (projectedMap && typeof projectedMap === 'object') {
    const all = total(obj);
    const proj = Math.min(total(projectedMap), all);
    return projected ? proj : all - proj;
  }
  return Object.entries(obj || {}).reduce((sum, [category, value]) => {
    const isProjected = projectedCategories.has(category);
    return isProjected === projected ? sum + Number(value || 0) : sum;
  }, 0);
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const entries = [
    ['Documented inflows', row.inflowSolid],
    ['Projected inflows', row.inflowProjected],
    ['Documented outflows', row.outflowSolid],
    ['Projected outflows', row.outflowProjected],
    // Overdue AP/PO is deliberately kept in its due bucket (never date-spread —
    // it can be demanded any day); the annotation is how the clump stays legible.
    ...(Number(row.overdueOut) > 0.01 ? [['— of which overdue', -Number(row.overdueOut)]] : []),
    ['Closing cash', row.closing],
  ];

  return (
    <Paper sx={{ p: 1.5, boxShadow: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      {entries.map(([name, value]) => (
        <Stack key={name} direction="row" justifyContent="space-between" spacing={3}>
          <Typography variant="caption" color="text.secondary">{name}</Typography>
          <Typography variant="caption" fontWeight={600}>{formatCurrency(value)}</Typography>
        </Stack>
      ))}
    </Paper>
  );
}

// CSV helpers shared by the events drawer export and the cash popup export.
const csvEscape = (val) => {
  const s = String(val ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
// Neutralize spreadsheet formula injection in free-text cells: Excel/Sheets
// treat a leading =, +, -, @, tab or CR as a formula, so prefix those with a
// single quote. Deliberately NOT applied to our own numeric amount — a genuine
// leading '-' on a negative figure must stay a number, not become text.
const csvText = (val) => {
  let s = String(val ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return csvEscape(s);
};
// Prepend a UTF-8 BOM so Excel opens the file with correct encoding.
const downloadCsv = (lines, baseName) => {
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  const safeName = String(baseName || 'export').replace(/[^\w.-]+/g, '_');
  anchor.download = `${safeName}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

// Native-currency balance (with cents) for the per-account cash popup. Falls
// back to "<amount> <code>" for a code Intl does not know.
function formatNative(value, currency) {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format(n);
  } catch (e) {
    return `${n.toFixed(2)} ${currency || ''}`.trim();
  }
}

function KpiCard({ icon, label, value, detail, tone = 'default', onClick }) {
  const palette = {
    default: { bg: 'background.paper', border: 'divider' },
    good: { bg: '#f0fdf4', border: '#bbf7d0' },
    warn: { bg: '#fffbeb', border: '#fde68a' },
    bad: { bg: '#fef2f2', border: '#fecaca' },
  }[tone];

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
      sx={{
        height: '100%',
        bgcolor: palette.bg,
        borderColor: palette.border,
        ...(onClick ? { cursor: 'pointer', '&:hover': { boxShadow: 2 } } : {}),
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          {icon}
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700}>{value}</Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {detail}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function CashFlowProjections() {
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [horizonMonths, setHorizonMonths] = useState(6);
  const [bucket, setBucket] = useState('week');
  const [tiers, setTiers] = useState(ALL_TIERS);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [assumptionsForm, setAssumptionsForm] = useState(null);
  const [assumptionsSaving, setAssumptionsSaving] = useState(false);
  const [plannedOpen, setPlannedOpen] = useState(false);
  const [plannedPos, setPlannedPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [plannedSaving, setPlannedSaving] = useState(false);
  const [plannedForm, setPlannedForm] = useState({
    supplier_id: '',
    description: '',
    amount: '',
    currency: 'USD',
    expected_po_date: new Date().toISOString().slice(0, 10),
  });
  const [eventsDrawer, setEventsDrawer] = useState({ open: false, loading: false, title: '', events: [], error: null });
  const [planDialog, setPlanDialog] = useState({ open: false, invoiceId: null, description: '', currency: 'USD', maxAmount: 0, date: '', amount: '', saving: false, error: null });
  const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, invoiceId: null, description: '', currency: 'USD', maxAmount: 0, amount: '', reference: '', saving: false, error: null });
  const [cashDialogOpen, setCashDialogOpen] = useState(false);

  const tierParam = useMemo(() => tiers.join(','), [tiers]);

  const loadProjection = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await cashflowProjectionService.getProjection({
        horizon_months: horizonMonths,
        bucket,
        tiers: tierParam,
      });
      setProjection(data);
    } catch (err) {
      console.error('Error loading cash flow projection:', err);
      setError(err.response?.data?.error || 'Failed to load cash flow projection.');
    } finally {
      setLoading(false);
    }
  }, [bucket, horizonMonths, tierParam]);

  useEffect(() => {
    loadProjection();
  }, [loadProjection]);

  const toggleTier = (tier) => {
    setTiers((current) => {
      if (current.includes(tier)) {
        return current.length === 1 ? current : current.filter((t) => t !== tier);
      }
      return ALL_TIERS.filter((t) => current.concat(tier).includes(t));
    });
  };

  const openAssumptions = async () => {
    try {
      setError(null);
      const data = await cashflowProjectionService.getAssumptions();
      setAssumptionsForm({
        revenue_growth_pct: Number(data.revenue_growth_pct || 0),
        collection_delay_days: Number(data.collection_delay_days || 0),
        amex_pay_day: Number(data.amex_pay_day || 15),
        min_cash_threshold: data.min_cash_threshold ?? '',
        ap_stale_after_days: Number(data.ap_stale_after_days || 90),
        po_stale_after_days: Number(data.po_stale_after_days || 365),
        settlement_lag_days: toJsonText(data.settlement_lag_days, { shopify: 2, amazon: 14 }),
        vat_line: toJsonText(data.vat_line, []),
        vat_due_dates: toJsonText(data.vat_due_dates, {}),
        vat_cadence_days: toJsonText(data.vat_cadence_days, {}),
        opex_overrides: toJsonText(data.opex_overrides, {}),
      });
      setError(null); setAssumptionsOpen(true);
    } catch (err) {
      console.error('Error loading assumptions:', err);
      setError(err.response?.data?.error || 'Failed to load assumptions.');
    }
  };

  const saveAssumptions = async () => {
    try {
      setAssumptionsSaving(true);
      setError(null);
      const updates = {
        revenue_growth_pct: parseFiniteNumber(assumptionsForm.revenue_growth_pct, 'Revenue growth %', 0),
        collection_delay_days: parseFiniteNumber(assumptionsForm.collection_delay_days, 'Collection delay days', 0, { min: 0 }),
        amex_pay_day: parseFiniteNumber(assumptionsForm.amex_pay_day, 'Amex payment day', 15, { min: 1, max: 31 }),
        min_cash_threshold: parseFiniteNumber(assumptionsForm.min_cash_threshold, 'Minimum cash threshold', null, { allowNull: true }),
        ap_stale_after_days: parseFiniteNumber(assumptionsForm.ap_stale_after_days, 'AP stale after days', 90, { min: 1 }),
        po_stale_after_days: parseFiniteNumber(assumptionsForm.po_stale_after_days, 'PO stale after days', 365, { min: 1 }),
        settlement_lag_days: parseJsonField(assumptionsForm.settlement_lag_days, 'Settlement lags', {}, isPlainObject),
        vat_line: parseJsonField(assumptionsForm.vat_line, 'VAT/GST lines', [], Array.isArray),
        vat_due_dates: parseJsonField(assumptionsForm.vat_due_dates, 'VAT due dates', {}, isPlainObject),
        vat_cadence_days: parseJsonField(assumptionsForm.vat_cadence_days, 'VAT cadence days', {}, isPlainObject),
        opex_overrides: parseJsonField(assumptionsForm.opex_overrides, 'OpEx overrides', {}, isPlainObject),
      };
      await cashflowProjectionService.putAssumptions(updates);
      setAssumptionsOpen(false);
      await loadProjection();
    } catch (err) {
      console.error('Error saving assumptions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save assumptions.');
    } finally {
      setAssumptionsSaving(false);
    }
  };

  const openPlannedPos = async () => {
    try {
      setError(null);
      const [poRes, supplierRes] = await Promise.all([
        cashflowProjectionService.getPlannedPos(),
        getSuppliers({ is_active: true, include_intercompany: true, limit: 1000 }),
      ]);
      setPlannedPos(poRes.planned_pos || []);
      const supplierData = supplierRes?.data;
      setSuppliers(Array.isArray(supplierData) ? supplierData : (supplierData?.suppliers || []));
      setError(null); setPlannedOpen(true);
    } catch (err) {
      console.error('Error loading planned POs:', err);
      setError(err.response?.data?.error || 'Failed to load planned POs.');
    }
  };

  const createPlannedPo = async () => {
    try {
      setPlannedSaving(true);
      setError(null);
      const amount = parseFiniteNumber(plannedForm.amount, 'Planned PO amount', 0, { min: 0.01 });
      const currency = String(plannedForm.currency || '').trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Currency must be a 3-letter code');
      if (!String(plannedForm.expected_po_date || '').match(/^\d{4}-\d{2}-\d{2}$/)) {
        throw new Error('PO date must be a valid date');
      }
      if (!String(plannedForm.description || '').trim()) throw new Error('Description is required');
      await cashflowProjectionService.createPlannedPo({
        source: 'manual',
        supplier_id: plannedForm.supplier_id ? Number(plannedForm.supplier_id) : 0,
        description: plannedForm.description.trim(),
        amount,
        currency,
        expected_po_date: plannedForm.expected_po_date,
        status: 'active',
      });
      setPlannedForm({
        supplier_id: '',
        description: '',
        amount: '',
        currency: 'USD',
        expected_po_date: new Date().toISOString().slice(0, 10),
      });
      const poRes = await cashflowProjectionService.getPlannedPos();
      setPlannedPos(poRes.planned_pos || []);
      await loadProjection();
    } catch (err) {
      console.error('Error creating planned PO:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save planned PO.');
    } finally {
      setPlannedSaving(false);
    }
  };

  const setPlannedStatus = async (id, status) => {
    try {
      setPlannedSaving(true);
      setError(null);
      await cashflowProjectionService.patchPlannedPo(id, { status });
      const poRes = await cashflowProjectionService.getPlannedPos();
      setPlannedPos(poRes.planned_pos || []);
      await loadProjection();
    } catch (err) {
      console.error('Error updating planned PO:', err);
      setError(err.response?.data?.error || 'Failed to update planned PO.');
    } finally {
      setPlannedSaving(false);
    }
  };

  const openEvents = async (bucketRow, category) => {
    if (!category || category === 'net' || category === 'closing') return;
    const title = `${categoryLabels[category] || category} · ${bucketRow.label}`;
    setEventsDrawer({ open: true, loading: true, title, events: [], error: null });
    try {
      const res = await cashflowProjectionService.getEvents({
        from: bucketRow.start,
        to: bucketRow.end,
        category,
        tiers: tierParam,
      });
      setEventsDrawer({ open: true, loading: false, title, events: res.events || [], error: null });
    } catch (err) {
      console.error('Error loading cashflow events:', err);
      setEventsDrawer({
        open: true,
        loading: false,
        title,
        events: [],
        error: err.response?.data?.error || 'Failed to load events.',
      });
    }
  };

  const exportEventsCsv = () => {
    const rows = eventsDrawer.events || [];
    if (!rows.length) return;
    const headers = ['Date', 'Placed', 'Payable', 'Description', 'Tier', 'Stale (excluded)', 'Source', 'Amount (USD)'];
    const lines = [headers.join(',')];
    rows.forEach((event) => {
      const amount = Number(event.amount_usd ?? event.usd ?? event.amount ?? 0);
      const direction = event.direction === 'out' ? -1 : 1;
      const signed = direction * Math.abs(amount);
      lines.push([
        csvEscape(formatDate(event.date)),
        csvEscape(event.source_date ? formatDate(event.source_date) : ''),
        csvEscape(event.due_original ? formatDate(event.due_original) : ''),
        csvText(event.description || ''),
        csvText(event.confidence || event.tier || ''),
        csvEscape(event.stale ? 'yes' : 'no'),
        csvText(`${event.source_type || ''} ${event.source_ref || ''}`.trim()),
        csvEscape(signed.toFixed(2)),
      ].join(','));
    });
    downloadCsv(lines, eventsDrawer.title || 'cashflow-events');
  };

  // Per-account cash popup → CSV: one row per (account, currency) with native
  // balance and USD, section subtotals, the grand total, FX-dropped rows, and
  // the feed timestamps so the file is self-describing.
  const exportCashCsv = () => {
    const oc = projection?.opening_cash;
    if (!oc) return;
    const lines = [['Section', 'Account', 'Source', 'Currency', 'Balance', 'USD'].join(',')];
    const money = (v) => csvEscape(Number(v || 0).toFixed(2));
    const pushRows = (section, rows) => {
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        lines.push([csvEscape(section), csvText(r.name || ''), csvText(r.source || ''), csvText(r.currency || ''), money(r.native), money(r.usd)].join(','));
      });
    };
    pushRows('Bank accounts', oc.bank);
    lines.push(['Bank accounts', 'Subtotal', '', 'USD', '', money(oc.bank_total_usd)].join(','));
    pushRows('Payment processors', oc.processors);
    lines.push(['Payment processors', 'Subtotal', '', 'USD', '', money(oc.processors_total_usd)].join(','));
    lines.push(['Total', 'Current cash', '', 'USD', '', money(oc.total_usd)].join(','));
    (Array.isArray(oc.dropped_fx) ? oc.dropped_fx : []).forEach((d) => {
      lines.push(['Excluded (no USD rate)', csvText(d.name || ''), csvText(d.source || ''), csvText(d.currency || ''), money(d.native), ''].join(','));
    });
    lines.push('');
    lines.push(['As of (UTC)', csvEscape(String(oc.as_of || ''))].join(','));
    Object.entries(oc.sources_as_of || {}).forEach(([src, ts]) => {
      lines.push([csvEscape(`Feed as of: ${src}`), csvEscape(String(ts || ''))].join(','));
    });
    downloadCsv(lines, `current-cash-by-account-${String(oc.as_of || '').slice(0, 10) || 'today'}`);
  };

  const openPlanDialog = (event) => {
    setPlanDialog({
      open: true,
      invoiceId: Number(event.source_ref),
      description: event.description || '',
      currency: event.currency || 'USD',
      maxAmount: Math.abs(Number(event.amount_native ?? event.amount ?? 0)),
      date: '',
      amount: String(Math.abs(Number(event.amount_native ?? event.amount ?? 0))),
      saving: false,
      error: null,
    });
  };

  const submitPlan = async () => {
    setPlanDialog((p) => ({ ...p, saving: true, error: null }));
    try {
      await cashflowProjectionService.planPayment({
        invoice_id: planDialog.invoiceId,
        scheduled_date: planDialog.date,
        amount: Number(planDialog.amount),
      });
      setPlanDialog((p) => ({ ...p, open: false, saving: false }));
      setEventsDrawer((d) => ({ ...d, open: false }));
      await loadProjection();
    } catch (err) {
      setPlanDialog((p) => ({ ...p, saving: false, error: err.response?.data?.error || 'Failed to plan payment.' }));
    }
  };

  const cancelPlan = async (event) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Cancel this planned payment?\n\n${event.description || ''}`)) return;
    try {
      await cashflowProjectionService.cancelPlannedPayment(Number(event.source_ref));
      setEventsDrawer((d) => ({ ...d, open: false }));
      await loadProjection();
    } catch (err) {
      setEventsDrawer((d) => ({ ...d, error: err.response?.data?.error || 'Failed to cancel planned payment.' }));
    }
  };

  const openMarkPaidDialog = (event) => {
    setMarkPaidDialog({
      open: true,
      invoiceId: Number(event.source_ref),
      description: event.description || '',
      currency: event.currency || 'USD',
      maxAmount: Math.abs(Number(event.amount_native ?? event.amount ?? 0)),
      amount: String(Math.abs(Number(event.amount_native ?? event.amount ?? 0))),
      reference: '',
      saving: false,
      error: null,
    });
  };

  const submitMarkPaid = async () => {
    setMarkPaidDialog((p) => ({ ...p, saving: true, error: null }));
    try {
      await cashflowProjectionService.markInvoicePaid(markPaidDialog.invoiceId, {
        amount: Number(markPaidDialog.amount),
        reference: markPaidDialog.reference,
      });
      setMarkPaidDialog((p) => ({ ...p, open: false, saving: false }));
      setEventsDrawer((d) => ({ ...d, open: false }));
      await loadProjection();
    } catch (err) {
      setMarkPaidDialog((p) => ({ ...p, saving: false, error: err.response?.data?.error || 'Failed to mark paid.' }));
    }
  };

  const openingCash = projection?.opening_cash?.total_usd || 0;
  const glVariance = projection?.opening_cash?.gl_variance_usd || 0;
  const trough = projection?.trough;
  const thresholdBreach = projection?.threshold_breach;
  const stale = projection?.meta?.stale_excluded;
  // Name EVERY stale category, not a hardcoded two. The banner used to print
  // `stale.by_category.ap_scheduled` and `.po_open_milestones` literally, so when a
  // third generator started emitting stale events (marketing_ap, deployed
  // 2026-08-13) its figure vanished from the text while still counting toward
  // usd_total — the banner's own parts stopped summing to its own total (measured
  // live: named 204,332.04 against a stated 285,718.67, an unexplained 81,386.63).
  // Iterating means any future generator that starts flagging stale events is
  // represented automatically instead of silently widening the gap again.
  // Non-zero (not >0) so a negative category can never be quietly dropped either —
  // reconciling with the total is the whole point of this line.
  const staleBreakdown = useMemo(() => {
    const byCategory = stale?.by_category;
    if (!byCategory) return null;
    const parts = Object.entries(byCategory)
      .filter(([, value]) => Number(value) !== 0)
      .sort((a, b) => Math.abs(Number(b[1])) - Math.abs(Number(a[1])))
      .map(([key, value]) => `${categoryLabels[key] || key} ${formatCurrency(value)}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [stale]);
  const categories = useMemo(
    () => (Array.isArray(projection?.meta?.categories) ? projection.meta.categories : []),
    [projection],
  );
  const buckets = useMemo(
    () => (Array.isArray(projection?.buckets) ? projection.buckets : []),
    [projection],
  );

  const chartData = useMemo(() => buckets.map((row) => ({
    label: row.label,
    inflowSolid: chartTotal(row.inflows, false, row.projected_in),
    inflowProjected: chartTotal(row.inflows, true, row.projected_in),
    outflowSolid: -chartTotal(row.outflows, false, row.projected_out),
    outflowProjected: -chartTotal(row.outflows, true, row.projected_out),
    overdueOut: total(row.overdue_out),
    closing: Number(row.closing_usd || 0),
  })), [buckets]);

  const gridRows = useMemo(() => {
    const inflowCategories = categories.filter((c) => buckets.some((b) => Number(b.inflows?.[c] || 0) !== 0));
    const outflowCategories = categories.filter((c) => buckets.some((b) => Number(b.outflows?.[c] || 0) !== 0));
    return [
      ...inflowCategories.map((category) => ({ type: 'category', direction: 'inflow', category })),
      ...outflowCategories.map((category) => ({ type: 'category', direction: 'outflow', category })),
      { type: 'net', category: 'net' },
      { type: 'closing', category: 'closing' },
    ];
  }, [categories, buckets]);

  const troughTone = trough && Number(trough.closing_usd) < 0 ? 'bad' : 'good';

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>Cash Flow Projection</Typography>
          <Typography variant="body2" color="text.secondary">
            Bank-truth cash projected forward from receivables, payables, PO milestones, revenue, and recurring vendor outflows.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <ButtonGroup size="small" variant="outlined">
            {[3, 6, 12].map((m) => (
              <Button
                key={m}
                variant={horizonMonths === m ? 'contained' : 'outlined'}
                onClick={() => setHorizonMonths(m)}
              >
                {m}m
              </Button>
            ))}
          </ButtonGroup>
          <ButtonGroup size="small" variant="outlined">
            {['day', 'week', 'month'].map((b) => (
              <Button
                key={b}
                variant={bucket === b ? 'contained' : 'outlined'}
                onClick={() => setBucket(b)}
              >
                {b}
              </Button>
            ))}
          </ButtonGroup>
          <Tooltip title="Refresh projection">
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadProjection}>
              Refresh
            </Button>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={openAssumptions}>
            Assumptions
          </Button>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openPlannedPos}>
            Planned POs
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {ALL_TIERS.map((tier) => (
          <Chip
            key={tier}
            label={tier}
            color={tiers.includes(tier) ? 'primary' : 'default'}
            variant={tiers.includes(tier) ? 'filled' : 'outlined'}
            onClick={() => toggleTier(tier)}
            size="small"
          />
        ))}
        {projection?.meta?.reorder_cache?.cache_status === 'cold' && (
          <Chip
            icon={<WarningAmberIcon />}
            label="Reorder cache cold"
            color="warning"
            variant="outlined"
            size="small"
          />
        )}
        {(() => {
          // Visible without hover, deliberately: overdue AP/PO stays in its due
          // bucket (never date-spread — it can be demanded any day); this chip
          // keeps the big near-term bar legible from across the room. Overdue
          // clamps to today+7, which can land in bucket 1 OR 2 depending on the
          // partial first week — so target the first bucket that carries any.
          const ob = buckets.find((b) => total(b?.overdue_out) > 0.01);
          return ob ? (
            <Chip
              icon={<WarningAmberIcon />}
              label={`${ob.label} includes overdue ${formatCurrency(total(ob.overdue_out))}`}
              color="warning"
              variant="outlined"
              size="small"
            />
          ) : null;
        })()}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {stale && stale.count > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {staleBreakdown
            ? <>{staleBreakdown} — {stale.count} items totaling {formatCurrency(stale.usd_total)} are excluded from the projected balance.</>
            : <>{stale.count} stale items totaling {formatCurrency(stale.usd_total)} are excluded from the projected balance.</>}
        </Alert>
      )}

      {Array.isArray(projection?.meta?.warnings) && projection.meta.warnings.length > 0 && (
        // One Alert per severity class so a payable that's INVISIBLE to the
        // headline number (error) can't hide between documented FYIs (info).
        ['error', 'warning', 'info'].map((sev) => {
          const items = projection.meta.warnings.filter((w) => (w.severity || 'info') === sev);
          if (items.length === 0) return null;
          return (
            <Alert key={sev} severity={sev} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {sev === 'error' ? 'Projection blind spots' : sev === 'warning' ? 'Ledger / data checks' : 'Notes'} ({items.length})
              </Typography>
              {items.map((w, i) => (
                <Typography key={`${w.type}-${i}`} variant="body2" sx={{ mb: 0.25 }}>
                  • {w.message || w.type}
                </Typography>
              ))}
            </Alert>
          );
        })
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <KpiCard
                icon={<AccountBalanceWalletIcon color="primary" />}
                label="Current cash"
                value={formatCurrency(openingCash)}
                detail={
                  (projection?.opening_cash?.stale_sources || []).length > 0
                    ? `GL variance ${formatCurrency(glVariance)} · STALE feeds: ${projection.opening_cash.stale_sources.map((s) => `${s.source} (${String(s.newest).slice(0, 10)})`).join(', ')} · click for accounts`
                    : `GL variance ${formatCurrency(glVariance)} · click for accounts`
                }
                tone={(projection?.opening_cash?.stale_sources || []).length > 0 || Math.abs(glVariance) > 100000 ? 'warn' : 'default'}
                onClick={() => setCashDialogOpen(true)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <KpiCard
                icon={<TrendingDownIcon color={troughTone === 'bad' ? 'error' : 'success'} />}
                label="Cash trough"
                value={formatCurrency(trough?.closing_usd)}
                detail={trough ? formatDate(trough.date) : 'No projection'}
                tone={troughTone}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <KpiCard
                icon={<TimelineIcon color="primary" />}
                label="Runway"
                value={thresholdBreach ? formatDate(thresholdBreach.date) : 'No breach'}
                detail={thresholdBreach ? `Threshold ${formatCurrency(thresholdBreach.threshold_usd)}` : `${horizonMonths} month horizon`}
              />
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Category coverage</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={categoryLabels[category] || category}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Projection trend</Typography>
              <Box sx={{ height: 360 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={chartData.length > 18 ? 1 : 0} />
                    <YAxis tickFormatter={(value) => formatCompactCurrency(value)} tick={{ fontSize: 12 }} />
                    <RechartsTooltip content={<ChartTooltip />} />
                    <Legend />
                    <ReferenceLine y={0} stroke="#6b7280" />
                    <Bar dataKey="inflowSolid" name="Inflows" stackId="inflows" fill="#16a34a" />
                    <Bar dataKey="inflowProjected" name="Projected inflows" stackId="inflows" fill="#16a34a" fillOpacity={0.45} />
                    <Bar dataKey="outflowSolid" name="Outflows" stackId="outflows" fill="#dc2626" />
                    <Bar dataKey="outflowProjected" name="Projected outflows" stackId="outflows" fill="#dc2626" fillOpacity={0.45} />
                    <Line
                      type="monotone"
                      dataKey="closing"
                      name="Closing cash"
                      stroke="#1d4ed8"
                      strokeWidth={2.5}
                      dot={false}
                      strokeDasharray={tiers.includes('projected') ? '5 4' : undefined}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Solid = documented (committed/expected) · faded = projected — the split follows each event's own confidence, not a fixed category list.
              </Typography>
            </CardContent>
          </Card>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader sx={{ minWidth: Math.max(760, buckets.length * 112 + 220) }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      bgcolor: 'background.paper',
                      minWidth: 220,
                    }}
                  >
                    Category
                  </TableCell>
                  {buckets.map((row) => (
                    <TableCell key={row.start} align="right">
                      <Typography variant="caption" fontWeight={700}>{row.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {gridRows.map((gridRow) => (
                  <TableRow key={`${gridRow.type}-${gridRow.category}`} hover>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" fontWeight={gridRow.type === 'category' ? 500 : 700}>
                        {categoryLabels[gridRow.category] || (gridRow.type === 'net' ? 'Net cash flow' : 'Closing cash')}
                      </Typography>
                      {gridRow.type === 'category' && (
                        <Typography variant="caption" color="text.secondary">
                          {gridRow.direction === 'inflow' ? 'Inflow' : 'Outflow'}
                        </Typography>
                      )}
                    </TableCell>
                    {buckets.map((row) => {
                      let value;
                      if (gridRow.type === 'net') value = Number(row.net_usd || 0);
                      else if (gridRow.type === 'closing') value = Number(row.closing_usd || 0);
                      else value = categoryTotal(row, gridRow.category);
                      const color = gridRow.type === 'closing'
                        ? (value < 0 ? 'error.main' : 'text.primary')
                        : (value < 0 ? 'error.main' : value > 0 ? 'success.main' : 'text.secondary');
                      return (
                        <TableCell
                          key={`${row.start}-${gridRow.category}`}
                          align="right"
                          onClick={() => gridRow.type === 'category' && openEvents(row, gridRow.category)}
                          sx={{ cursor: gridRow.type === 'category' ? 'pointer' : 'default' }}
                        >
                          <Typography variant="body2" color={color} fontWeight={gridRow.type === 'closing' ? 700 : 400}>
                            {value === 0 ? '-' : formatCompactCurrency(value)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Inflows</TableCell>
                  <TableCell align="right">Outflows</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell align="right">Closing cash</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {buckets.map((row) => {
                  const inflows = total(row.inflows);
                  const outflows = total(row.outflows);
                  return (
                    <TableRow key={`${row.start}-${row.end}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(row.start)} - {formatDate(row.end)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(inflows)}</TableCell>
                      <TableCell align="right">{formatCurrency(outflows)}</TableCell>
                      <TableCell align="right">
                        <Typography color={Number(row.net_usd) < 0 ? 'error.main' : 'success.main'}>
                          {formatCurrency(row.net_usd)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700}>{formatCurrency(row.closing_usd)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Drawer anchor="right" open={assumptionsOpen} onClose={() => setAssumptionsOpen(false)}>
        <Box sx={{ width: { xs: 360, sm: 480 }, p: 3 }}>
          <Typography variant="h6" gutterBottom>Projection assumptions</Typography>
          {/* DT-1922: this drawer stays open when its save fails; the page Alert sits behind its backdrop. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {assumptionsForm ? (
            <Stack spacing={2}>
              <TextField
                label="Revenue growth %"
                type="number"
                size="small"
                value={assumptionsForm.revenue_growth_pct}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, revenue_growth_pct: e.target.value })}
              />
              <TextField
                label="Collection delay days"
                type="number"
                size="small"
                value={assumptionsForm.collection_delay_days}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, collection_delay_days: e.target.value })}
              />
              <TextField
                label="Amex payment day"
                type="number"
                size="small"
                value={assumptionsForm.amex_pay_day}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, amex_pay_day: e.target.value })}
              />
              <TextField
                label="Minimum cash threshold"
                type="number"
                size="small"
                value={assumptionsForm.min_cash_threshold}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, min_cash_threshold: e.target.value })}
              />
              <TextField
                label="AP stale after days"
                type="number"
                size="small"
                value={assumptionsForm.ap_stale_after_days}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, ap_stale_after_days: e.target.value })}
              />
              <TextField
                label="PO stale after days"
                type="number"
                size="small"
                value={assumptionsForm.po_stale_after_days}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, po_stale_after_days: e.target.value })}
              />
              <TextField
                label="Settlement lag days JSON"
                multiline
                minRows={3}
                size="small"
                value={assumptionsForm.settlement_lag_days}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, settlement_lag_days: e.target.value })}
              />
              <TextField
                label="VAT/GST lines JSON"
                multiline
                minRows={4}
                size="small"
                value={assumptionsForm.vat_line}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, vat_line: e.target.value })}
                helperText={'Example: [{"date":"2026-09-30","amount":25000,"currency":"GBP"}]'}
              />
              <TextField
                label="Monthly OpEx overrides JSON"
                multiline
                minRows={4}
                size="small"
                value={assumptionsForm.opex_overrides}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, opex_overrides: e.target.value })}
                helperText={'Example: {"payroll":50000,"rent":8000}'}
              />
              <TextField
                label="VAT/GST due dates JSON"
                multiline
                minRows={3}
                size="small"
                value={assumptionsForm.vat_due_dates}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, vat_due_dates: e.target.value })}
                helperText={'Accountant-confirmed next filing due date per jurisdiction; also makes an unverified jurisdiction live. Example: {"uk":"2026-08-31","au":"2026-08-28","eu_oss":"2026-10-31"}'}
              />
              <TextField
                label="VAT/GST cadence days JSON"
                multiline
                minRows={3}
                size="small"
                value={assumptionsForm.vat_cadence_days}
                onChange={(e) => setAssumptionsForm({ ...assumptionsForm, vat_cadence_days: e.target.value })}
                helperText={'Filing interval override per jurisdiction (30 = monthly, 91 = quarterly). Example: {"uk":91,"au":91}'}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => setAssumptionsOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={saveAssumptions} disabled={assumptionsSaving}>
                  Save changes
                </Button>
              </Stack>
            </Stack>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Drawer>

      <Dialog open={plannedOpen} onClose={() => setPlannedOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Planned POs</DialogTitle>
        <DialogContent dividers>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Supplier</InputLabel>
                <Select
                  label="Supplier"
                  value={plannedForm.supplier_id}
                  onChange={(e) => setPlannedForm({ ...plannedForm, supplier_id: e.target.value })}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.supplier_name || s.name || `Supplier ${s.id}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={plannedForm.description}
                onChange={(e) => setPlannedForm({ ...plannedForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                type="number"
                value={plannedForm.amount}
                onChange={(e) => setPlannedForm({ ...plannedForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                fullWidth
                size="small"
                label="Currency"
                value={plannedForm.currency}
                onChange={(e) => setPlannedForm({ ...plannedForm, currency: e.target.value.toUpperCase().slice(0, 3) })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="PO date"
                type="date"
                value={plannedForm.expected_po_date}
                onChange={(e) => setPlannedForm({ ...plannedForm, expected_po_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="contained" onClick={createPlannedPo} disabled={plannedSaving}>
                Add
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>PO date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plannedPos.map((po) => {
                  const supplier = suppliers.find((s) => Number(s.id) === Number(po.supplier_id));
                  return (
                    <TableRow key={po.id}>
                      <TableCell>{po.description}</TableCell>
                      <TableCell>{supplier?.supplier_name || supplier?.name || (po.supplier_id ? `#${po.supplier_id}` : 'Unassigned')}</TableCell>
                      <TableCell>{formatDate(po.expected_po_date)}</TableCell>
                      <TableCell align="right">{formatCurrency(po.amount, po.currency || 'USD')}</TableCell>
                      <TableCell><Chip label={po.status} size="small" /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {po.status !== 'active' && (
                            <Button size="small" onClick={() => setPlannedStatus(po.id, 'active')} disabled={plannedSaving}>
                              Activate
                            </Button>
                          )}
                          {po.status !== 'dismissed' && (
                            <Button size="small" color="warning" onClick={() => setPlannedStatus(po.id, 'dismissed')} disabled={plannedSaving}>
                              Dismiss
                            </Button>
                          )}
                          {po.status !== 'converted' && (
                            <Button size="small" onClick={() => setPlannedStatus(po.id, 'converted')} disabled={plannedSaving}>
                              Converted
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {plannedPos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">No planned POs yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlannedOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="bottom" open={eventsDrawer.open} onClose={() => setEventsDrawer({ ...eventsDrawer, open: false })}>
        <Box sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6">{eventsDrawer.title}</Typography>
            {!eventsDrawer.loading && !eventsDrawer.error && eventsDrawer.events.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportEventsCsv}
                sx={{ flexShrink: 0 }}
              >
                Export CSV
              </Button>
            )}
          </Stack>
          {eventsDrawer.loading ? (
            <CircularProgress />
          ) : eventsDrawer.error ? (
            <Alert severity="error">{eventsDrawer.error}</Alert>
          ) : (
            <>
            {(() => {
              const sum = (pred) => eventsDrawer.events.reduce(
                (s, e) => s + (pred(e) ? Number(e.amount_usd ?? e.usd ?? e.amount ?? 0) : 0), 0);
              const counted = sum((e) => !e.stale);
              const staleSum = sum((e) => e.stale);
              return staleSum > 0.005 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Counted in this bucket's total: {formatCurrency(counted)} · stale (excluded from total): {formatCurrency(staleSum)}
                </Alert>
              ) : null;
            })()}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Placed</TableCell>
                    <TableCell>Payable</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eventsDrawer.events.map((event, idx) => {
                    const amount = Number(event.amount_usd ?? event.usd ?? event.amount ?? 0);
                    const direction = event.direction === 'out' ? -1 : 1;
                    const sourceLink = event.source_type === 'purchase_orders'
                      ? `/procurement/purchase-orders/${event.source_ref}` : null;
                    return (
                      <TableRow key={`${event.source_type}-${event.source_ref}-${idx}`}>
                        <TableCell>{formatDate(event.date)}</TableCell>
                        <TableCell>{event.source_date ? formatDate(event.source_date) : '—'}</TableCell>
                        <TableCell>{event.due_original ? formatDate(event.due_original) : '—'}</TableCell>
                        <TableCell>{event.description}</TableCell>
                        <TableCell>
                          <Chip label={event.confidence || event.tier || '-'} size="small" />
                          {event.stale && (
                            <Tooltip title="Excluded from the projected balance — older than the AP/PO staleness threshold. A cleanup item, not a live commitment.">
                              <Chip label="stale · excluded" size="small" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {sourceLink ? (
                            <Button size="small" href={sourceLink}>{event.source_ref}</Button>
                          ) : (
                            <Typography variant="body2">{event.source_type} {event.source_ref}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {event.category === 'ap_scheduled' && event.source_type === 'supplier_invoices' && event.direction === 'out' && (
                            <>
                              <Button size="small" href={`/procurement/invoices/${event.source_ref}`} target="_blank" rel="noopener" sx={{ minWidth: 0 }}>Open</Button>
                              <Button size="small" onClick={() => openPlanDialog(event)} sx={{ minWidth: 0 }}>Plan</Button>
                              <Button size="small" color="success" onClick={() => openMarkPaidDialog(event)} sx={{ minWidth: 0 }}>Mark paid</Button>
                            </>
                          )}
                          {event.source_type === 'invoice_payment_schedule' && (event.description || '').includes('Planned by user') && (
                            <Button size="small" color="warning" onClick={() => cancelPlan(event)} sx={{ minWidth: 0 }}>Cancel plan</Button>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(direction * Math.abs(amount))}</TableCell>
                      </TableRow>
                    );
                  })}
                  {eventsDrawer.events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">No events in this bucket.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </Box>
      </Drawer>

      <Dialog open={planDialog.open} onClose={() => setPlanDialog((p) => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Plan payment date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{planDialog.description}</Typography>
          {planDialog.error && <Alert severity="error" sx={{ mb: 2 }}>{planDialog.error}</Alert>}
          <TextField
            label="Pay on"
            type="date"
            fullWidth
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().slice(0, 10) }}
            value={planDialog.date}
            onChange={(e) => setPlanDialog((p) => ({ ...p, date: e.target.value }))}
          />
          <TextField
            label={`Amount (${planDialog.currency})`}
            type="number"
            fullWidth
            inputProps={{ min: 0.01, max: planDialog.maxAmount }}
            error={Number(planDialog.amount) > planDialog.maxAmount}
            value={planDialog.amount}
            onChange={(e) => setPlanDialog((p) => ({ ...p, amount: e.target.value }))}
            helperText={`Outstanding on this event: ${planDialog.maxAmount.toLocaleString()} ${planDialog.currency}. A smaller amount leaves the remainder at the original due date.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog((p) => ({ ...p, open: false }))}>Close</Button>
          <Button
            variant="contained"
            disabled={planDialog.saving || !planDialog.date || !(Number(planDialog.amount) > 0)}
            onClick={submitPlan}
          >
            {planDialog.saving ? 'Saving…' : 'Plan payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={markPaidDialog.open} onClose={() => setMarkPaidDialog((p) => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Mark invoice paid</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{markPaidDialog.description}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Records the payment on the AP ledger (documents level — no journal entries). Use the bank reference so the payment stays auditable.
          </Typography>
          {markPaidDialog.error && <Alert severity="error" sx={{ mb: 2 }}>{markPaidDialog.error}</Alert>}
          <TextField
            label={`Amount paid (${markPaidDialog.currency})`}
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 0.01, max: markPaidDialog.maxAmount }}
            error={Number(markPaidDialog.amount) > markPaidDialog.maxAmount}
            value={markPaidDialog.amount}
            onChange={(e) => setMarkPaidDialog((p) => ({ ...p, amount: e.target.value }))}
            helperText={`Outstanding: ${markPaidDialog.maxAmount.toLocaleString()} ${markPaidDialog.currency}. A smaller amount records a partial payment.`}
          />
          <TextField
            label="Bank reference (optional but recommended)"
            fullWidth
            value={markPaidDialog.reference}
            onChange={(e) => setMarkPaidDialog((p) => ({ ...p, reference: e.target.value }))}
            placeholder="e.g. revolut:6a4de159… or Wise 2026-07-08 GBP 360.00"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialog((p) => ({ ...p, open: false }))}>Close</Button>
          <Button
            variant="contained"
            color="success"
            disabled={markPaidDialog.saving || !(Number(markPaidDialog.amount) > 0) || Number(markPaidDialog.amount) > markPaidDialog.maxAmount}
            onClick={submitMarkPaid}
          >
            {markPaidDialog.saving ? 'Saving…' : 'Mark paid'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cashDialogOpen} onClose={() => setCashDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Current cash by account</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Latest balance per account from the bank and processor feeds, converted to USD at the latest monthly rate.
            {' '}Feeds as of:{' '}
            {Object.entries(projection?.opening_cash?.sources_as_of || {})
              .map(([src, ts]) => `${src} ${ts ? String(ts).slice(0, 16) : '—'}`)
              .join(' · ') || '—'}.
          </Typography>
          {[
            ['Bank accounts', projection?.opening_cash?.bank, projection?.opening_cash?.bank_total_usd],
            ['Payment processors', projection?.opening_cash?.processors, projection?.opening_cash?.processors_total_usd],
          ].map(([title, rows, subtotal]) => (
            <Box key={title} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">{title}</Typography>
                <Typography variant="subtitle2">{formatCurrency(subtotal)}</Typography>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="right">USD</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(rows) ? rows : []).map((r, i) => (
                      <TableRow key={`${r.source}-${r.name}-${r.currency}-${i}`}>
                        <TableCell>
                          <Typography variant="body2">{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.source}</Typography>
                        </TableCell>
                        <TableCell align="right">{formatNative(r.native, r.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(r.usd)}</TableCell>
                      </TableRow>
                    ))}
                    {(!Array.isArray(rows) || rows.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">No non-zero balances</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="subtitle1" fontWeight={700}>Total current cash</Typography>
            <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(openingCash)}</Typography>
          </Stack>
          {(projection?.opening_cash?.dropped_fx || []).length > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              Excluded (no USD rate): {projection.opening_cash.dropped_fx.map((d) => `${d.name} ${formatNative(d.native, d.currency)}`).join(', ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportCashCsv}
            disabled={!projection?.opening_cash}
          >
            Download CSV
          </Button>
          <Button onClick={() => setCashDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
